import { Typography, useDialog } from '@gyym/ui'
import React from 'react'

import usePermission from './usePermission'

export type Location = {
  lat: number
  lng: number
}

const FALLBACK_CENTER = {
  lat: 35.6809591,
  lng: 139.7673068,
}

const useGeolocation = ({
  skipInitialPrompt = false,
  enableHighAccuracy = true,
  fallbackCenter = FALLBACK_CENTER,
  ...options
}: PositionOptions & {
  skipInitialPrompt?: boolean
  fallbackCenter?: Location
} = {}) => {
  const optionsRef = React.useRef({ ...options, enableHighAccuracy })
  const hasStartedInitialRequestRef = React.useRef(false)
  const permissionState = usePermission('geolocation')
  const [isPrompted, setIsPrompted] = React.useState(false)
  const [isLocationResolved, setIsLocationResolved] = React.useState(false)
  const [currentLocation, setCurrentLocation] =
    React.useState<Location>(fallbackCenter)

  const { openDialog } = useDialog()

  const onBeforePrompt = React.useCallback((): Promise<boolean> => {
    setIsPrompted(true)

    return new Promise((resolve) => {
      openDialog({
        title: (
          <Typography variant="body1" color="gray.01">
            施設検索の為に位置情報を取得します。
            <br />
            よろしいですか？
          </Typography>
        ),
        disableBackdropClose: true,
        backdropOpacity: 0.2,
        positiveAction: {
          onClick: () => {
            resolve(true)
          },
        },
        negativeAction: {
          onClick: () => {
            resolve(false)
          },
        },
      })
    })
  }, [openDialog])

  const getLocation = React.useCallback(async () => {
    // Permission initializing
    if (permissionState === null) return fallbackCenter

    // Permission denied
    if (permissionState === 'denied') {
      setIsLocationResolved(true)
      return fallbackCenter
    }

    // Already prompted for permission
    if (permissionState === 'prompt' && isPrompted) {
      return fallbackCenter
    }

    // Prompt for permission
    if (permissionState === 'prompt' && !isPrompted) {
      const result = await onBeforePrompt()

      // User denied
      if (!result) {
        setIsLocationResolved(true)
        return fallbackCenter
      }
    }

    // Get location
    return new Promise<Location>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }
          setCurrentLocation(location)
          setIsLocationResolved(true)
          return resolve(location)
        },
        (error) => {
          // Allow callers to fall back to the configured center after the
          // browser has finished resolving the geolocation request.
          setIsLocationResolved(true)
          reject(error)
        },
        optionsRef.current,
      )
    })
  }, [onBeforePrompt, permissionState, fallbackCenter, isPrompted])

  // Get initial lat/lng
  React.useEffect(() => {
    if (
      skipInitialPrompt ||
      permissionState === null ||
      hasStartedInitialRequestRef.current
    )
      return

    // Permission / prompt state updates recreate getLocation. Only the first
    // resolved permission state should start the initial browser request.
    hasStartedInitialRequestRef.current = true
    void getLocation().catch(() => undefined)
  }, [getLocation, permissionState, skipInitialPrompt])

  return {
    currentLocation,
    getLocation,
    isLocationResolved,
    permissionState,
  }
}

export default useGeolocation
